import React, { useEffect, useState } from "react";
import {
  Home,
  ClipboardList,
  FlaskConical,
  TestTube2,
  DollarSign,
  ReceiptText,
  Settings,
  LogOut,
  X,
  ChevronRight,
  Activity,
  ListChecks,
  BarChart2,
  Wallet,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { canAccess } from "../../utils/auth";

const NAV_ITEMS = [
  { to: "/dashboard", icon: Home, label: "Dashboard" },
  { to: "/patients", icon: ClipboardList, label: "Pacientes" },
  { to: "/orders", icon: Activity, label: "Órdenes" },
  { to: "/results", icon: FlaskConical, label: "Resultados" },
  { to: "/worklist", icon: ListChecks, label: "Lista de trabajo" },
];

const ADMIN_ITEMS = [
  { to: "/indicadores", icon: BarChart2, label: "Indicadores" },
  { to: "/params", icon: TestTube2, label: "Valores de referencia" },
  { to: "/tarifas-examenes", icon: DollarSign, label: "Tarifas exámenes" },
  { to: "/invoices", icon: ReceiptText, label: "Facturas" },
  { to: "/caja", icon: Wallet, label: "Caja" },
  { to: "/config", icon: Settings, label: "Configuración" },
];

const NavLink = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onClick(item.to)}
      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
        isActive
          ? "bg-white/20 text-white shadow-sm"
          : "text-purple-200 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon
        className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-white" : "text-purple-300 group-hover:text-white"}`}
      />
      <span className="truncate">{item.label}</span>
      {isActive && <ChevronRight className="h-3 w-3 ml-auto text-white/60" />}
    </button>
  );
};

const Sidebar = ({ mobileOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const visibleAdminItems = ADMIN_ITEMS.filter((item) => canAccess(item.to));
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [empresa, setEmpresa] = useState("");

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || "";
    fetch(`${apiUrl}/util/param_sist`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.empresa) setEmpresa(d.empresa);
      })
      .catch(() => {});
  }, []);

  const doLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("permisos");
    sessionStorage.clear();
    navigate("/login");
    window.history.pushState(null, "", "/login");
  };

  const handleLogout = () => {
    const hasDraft =
      localStorage.getItem("patients_draft_order_active") === "1";
    const isPatients = window.location.pathname === "/patients";
    if (hasDraft && isPatients) {
      window.dispatchEvent(
        new CustomEvent("patients:attempt-leave", {
          detail: { action: "logout" },
        }),
      );
      return;
    }
    doLogout();
  };

  const tryNavigate = (to) => {
    const hasDraft =
      localStorage.getItem("patients_draft_order_active") === "1";
    const isPatients = window.location.pathname === "/patients";
    if (hasDraft && isPatients && to !== "/patients") {
      window.dispatchEvent(
        new CustomEvent("patients:attempt-leave", {
          detail: { action: "navigate", to },
        }),
      );
      return;
    }
    onClose?.();
    navigate(to);
  };

  const isActive = (to) =>
    to === "/dashboard"
      ? location.pathname === "/" || location.pathname === "/dashboard"
      : location.pathname.startsWith(to);

  return (
    <aside className="flex flex-col h-full bg-sidebar-gradient w-full overflow-hidden">
      {/* Header logo */}
      <div className="flex items-center justify-between px-4 py-5 border-b border-white/10">
        <button
          onClick={() => tryNavigate("/dashboard")}
          className="flex items-center gap-3 min-w-0"
        >
          <div className="flex-shrink-0 w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
            <img src="/3.png" alt="Logo" className="h-6 w-6 object-contain" />
          </div>

          <div className="min-w-0 text-left">
            <p className="text-white font-bold text-base leading-tight truncate">
              Vetronix
            </p>

            <p className="text-purple-300 text-xs truncate">
              {empresa || "Lab Veterinario"}
            </p>
          </div>
        </button>

        {/* Cerrar en móvil */}
        <button
          onClick={onClose}
          className="lg:hidden text-purple-300 hover:text-white transition-colors p-1"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navegación principal */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        <p className="text-purple-400 text-xs font-semibold uppercase tracking-wider px-4 mb-2">
          Principal
        </p>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            item={item}
            isActive={isActive(item.to)}
            onClick={tryNavigate}
          />
        ))}

        {visibleAdminItems.length > 0 && (
          <>
            <p className="text-purple-400 text-xs font-semibold uppercase tracking-wider px-4 mt-5 mb-2">
              Administración
            </p>
            {visibleAdminItems.map((item) => (
              <NavLink
                key={item.to}
                item={item}
                isActive={isActive(item.to)}
                onClick={tryNavigate}
              />
            ))}
          </>
        )}
      </nav>

      {/* Footer usuario */}
      <div className="border-t border-white/10 px-3 py-4">
        {/* Estado servidor */}
        <div className="flex items-center gap-2 px-4 mb-3">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-xs text-purple-300">Servidor en línea</span>
        </div>

        {/* Info usuario */}
        <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/10 mb-2">
          <img
            src={
              user?.foto && user.foto.startsWith("data:image")
                ? user.foto
                : "/user-avatar.png"
            }
            alt="Avatar"
            className="h-8 w-8 rounded-full object-cover border-2 border-purple-300 flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">
              {user?.nombre || "Usuario"}
            </p>
            <p className="text-purple-300 text-xs truncate">
              {user?.usuario || ""}
            </p>
          </div>
        </div>

        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-all duration-200"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
